import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { BottomSheet } from './BottomSheet';
import { CardTopUpManualSheet } from './CardTopUpManualSheet';
import { ReauthFlow } from './ReauthFlow';
import { SourceSelect, buildPaySources, pickDefaultPaySource } from './SourceSelect';
import { ProgressButton } from './ProgressButton';
import { WarningIcon } from './icons';
import { useMe } from '../../hooks/useMe';
import { useTelegram } from '../../hooks/useTelegram';
import { useWallet } from '../../hooks/useWallet';
import { useTransfers } from '../../hooks/useTransfers';
import { useCardTemplates } from '../../hooks/useCards';
import { useFeeQuote } from '../../hooks/useFeeQuote';
import {
  cardErrorMessage,
  cardPaySources,
  cardPayTo,
  checkCardRechargeEligibility,
  rechargeCard,
  type Card,
} from '../../lib/cards';
import { cardRechargeFeePct, cardRechargeMinUsd, subsidizedNetworkFee } from '@incpt/shared';

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

/** Top up a card: pay `amount` + recharge fee to the treasury, then credit the card.
 *  UI closes right after the treasury broadcast (same as a normal crypto send).
 *  Banana recharge + on-chain settlement run in the background; balance refreshes
 *  when CARD_RECHARGE_RESULT lands. */
export function TopUpSheet({
  card,
  open,
  onClose,
  onDone,
}: {
  card: Card | null;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { me, initData, links, canFetch } = useMe();
  const { userId } = useTelegram();
  const { wallet, balances } = useWallet();
  const { send } = useTransfers();
  const { templates, treasury } = useCardTemplates();
  const qc = useQueryClient();

  const [amount, setAmount] = useState('');
  const [sourceKey, setSourceKey] = useState<string | null>(null);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  /** `crediting` = broadcast done; `failed` = background recharge rejected payment. */
  const [phase, setPhase] = useState<'idle' | 'crediting' | 'failed'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [failedAt, setFailedAt] = useState<Date | null>(null);

  const tpl = templates.find((x) => x.templateId === card?.templateId);
  const feePct = cardRechargeFeePct(tpl?.rechargeFee, card?.templateId);
  const min = cardRechargeMinUsd(tpl?.rechargeMin);
  const amt = Number(amount) || 0;
  const fee = (amt * feePct) / 100;
  const payTotal = amt + fee;
  const amountOk = amt > 0 && amt >= min;
  const needsReauth = !!me?.pinSet || !!me?.biometricEnabled;

  // All stablecoin sources are selectable (incl. Ethereum). Default the picker to
  // the first USDT TRC-20 wallet when the user hasn't chosen one explicitly.
  // Only the chains the server gave a card destination for: a wallet on any
  // other chain cannot be paid from, and offering it ends in an "invalid
  // recipient" error that reads as the user's address being wrong.
  const sources = cardPaySources(buildPaySources(wallet, balances, t('nav.wallet')), treasury);
  const source = sources.find((s) => s.key === sourceKey) ?? pickDefaultPaySource(sources);
  // The server charges the network fee (gas) ON TOP of the transfer amount from
  // the SAME source address, so the balance must cover payTotal + fee — otherwise
  // the send fails with "insufficient_balance" even though payTotal alone fit.
  const feeTo = source ? cardPayTo(source.chain, treasury) : undefined;
  // Quoted as a card top-up so the network fee comes back under the card fee
  // policy. Deliberately without the amount: the quote is not keyed on it, and
  // the policy it returns is enough to re-derive the fee for every keystroke.
  const cardPayment = card ? ({ kind: 'topup', cardId: card.id } as const) : undefined;
  const {
    feeUsd: networkFee,
    maxFeeUsd,
    cardFee,
    fresh: feeFresh,
  } = useFeeQuote(
    source?.chain ?? 'trx',
    source?.token ?? 'USDT',
    feeTo ?? undefined,
    source?.address,
    cardPayment,
  );
  // Part of our slice that pays the network fee down, and the fee left after it.
  // Same pure function the API charges by, so the row can never promise a
  // discount the send does not honour.
  const policy = cardFee?.kind === 'topup' ? cardFee : null;
  const netFullFee = policy?.networkFeeUsd ?? networkFee ?? 0;
  const discount = (full: number) =>
    policy ? subsidizedNetworkFee(full, amt, policy.subsidyPct) : full;
  // Show the real fee; gate on the reserve (first Tron send may need headroom).
  // Paying before the quote is priced for THIS source would check the balance
  // against $0 and let the treasury send fail as insufficient_balance.
  const feeDisplay = discount(netFullFee);
  const feeReserve = discount(maxFeeUsd ?? netFullFee);
  // Show everything INCPT paid for the user: the amount-based subsidy plus a
  // one-time wallet warm-up gift (activation / approve), when present.
  const feeDiscount = Math.max(
    0,
    Math.round(((policy?.warmCoveredUsd ?? 0) + netFullFee - feeDisplay) * 100) / 100,
  );
  const totalWithFee = payTotal + feeDisplay;
  const totalReserved = payTotal + feeReserve;
  // React Query intentionally keeps the previous wallet's quote while fetching
  // the new one. It is never safe to display that stale number as if it belonged
  // to the newly selected source.
  const feeRepricing = !!source && !feeFresh;
  const canPay = amountOk && !!source && feeFresh && source.balance >= totalReserved;

  // Largest balance the user could pay this top-up from — same chain and token,
  // since switching to another asset re-prices the whole quote. Thresholds are
  // only offered as reachable when this covers amount + our %.
  const richest = source
    ? Math.max(
        ...sources
          .filter((s) => s.chain === source.chain && s.token === source.token)
          .map((s) => s.balance),
      )
    : 0;
  const reachable = (base: number) => base * (1 + feePct / 100) <= richest;

  const payIdemRef = useRef<string | null>(null);
  const submittingRef = useRef(false);
  /** Ignore stale background recharge results after a new open/submit. */
  const attemptRef = useRef(0);
  const [toastAmt, setToastAmt] = useState(0);

  // Reset transient state each time the sheet (re)opens, so a stale amount or a
  // spent payment idempotency key from a previous top-up never carries over.
  // Clear sourceKey so the first USDT TRC-20 wallet is preselected again.
  useEffect(() => {
    if (!open) return;
    // This used to be an empty value with a "$20" placeholder. It looked filled
    // while every fee row was actually calculated from zero ($0 product fee,
    // network-only total). Start from the real minimum instead.
    attemptRef.current += 1;
    setAmount(String(min));
    setSourceKey(null);
    setError(null);
    setPhase('idle');
    setFailedAt(null);
    payIdemRef.current = null;
  }, [open]);

  // After the "accepted / crediting" toast, close so the user can keep using the app.
  useEffect(() => {
    if (phase !== 'crediting') return;
    const id = window.setTimeout(onDone, 1600);
    return () => window.clearTimeout(id);
  }, [phase, onDone]);

  const submit = async (token?: string) => {
    if (!card || !canFetch || !wallet?.id || !source || submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      // Local card status may lag behind a provider/support lock. Check Banana
      // immediately before sending the irreversible USDT treasury payment.
      await checkCardRechargeEligibility(initData, card.id);
      const to = cardPayTo(source.chain, treasury);
      if (!to) throw new Error('card fee treasury not configured');
      if (!payIdemRef.current) payIdemRef.current = crypto.randomUUID();
      const transfer = await send.mutateAsync({
        walletId: wallet.id,
        chain: source.chain,
        token: source.token,
        to,
        amount: String(payTotal),
        reauthToken: token,
        fromAddressId: source.addressId,
        fromIndex: source.fromIndex,
        idempotencyKey: payIdemRef.current,
        // Same as a normal crypto send: return after broadcast. Server-side
        // verifyCardPayment waits for on-chain settlement before Banana recharge.
        cardPayment: { kind: 'topup', cardId: card.id, amount: amt },
      });
      const transferId = transfer.id;
      const cardId = card.id;
      const topUpAmt = amt;
      const attempt = ++attemptRef.current;
      payIdemRef.current = null;
      setToastAmt(topUpAmt);
      // Close at crypto-send speed; settle + Banana run in the background.
      setPhase('crediting');
      void (async () => {
        try {
          const result = await rechargeCard(
            initData,
            cardId,
            { amount: topUpAmt, payment_transfer_id: transferId },
            crypto.randomUUID(),
            token,
          );
          if (!result.ok) throw new Error('top_up_failed');
          if (attempt !== attemptRef.current) return;
          // Kick the pending-intent watcher; do NOT refetch balance yet — Banana
          // credits asynchronously and an early get_balance would cache a stale value.
          await qc.invalidateQueries({ queryKey: ['card-intents', 'recharge', 'pending'] });
        } catch (e) {
          if (attempt !== attemptRef.current) return;
          const msg = String((e as Error)?.message ?? e);
          // Merchant float empty: payment taken, ops will credit manually.
          // Keep the "in progress" toast — don't scare the user with the support sheet.
          if (/provider_float_empty/i.test(msg)) return;
          setError(cardErrorMessage(msg, t, t('cards.topUpFailed'), 'topUp'));
          setFailedAt(new Date());
          setPhase('failed');
        }
      })();
    } catch (e) {
      const msg = String((e as Error)?.message ?? e);
      // Keep the key only while the same broadcast is still reconciling. Terminal
      // sponsor/prep failures must mint a fresh attempt on the next tap.
      if (
        !/transfer_verification_pending/i.test(msg) &&
        /sponsored_resources_expired|preparation_lease_expired|transfer_failed|energy_rental_failed|sponsor_risk_limit/i.test(
          msg,
        )
      ) {
        payIdemRef.current = null;
      }
      setError(cardErrorMessage(msg, t, t('cards.topUpFailed'), 'topUp'));
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  const onConfirm = () => {
    if (!canPay) return;
    if (needsReauth) setReauthOpen(true);
    else void submit();
  };

  return (
    <>
      {/* Keep the sheet "open" during reauth so sheetCount stays > 0 (nav hidden).
          ReauthFlow also bumps sheetCount; either alone is enough, both is safe. */}
      <BottomSheet open={open && phase === 'idle'} onClose={onClose} title={t('cards.topUp')}>
        {card && (
          <div className="mt-4 flex flex-col gap-3">
            <label className="flex items-center justify-between rounded-[16px] bg-[#f6f7f8] px-4 py-3">
              <span className="text-[14px] text-muted">{t('cards.amount')}</span>
              <div className="flex items-center">
                <span className="text-[18px] font-medium text-ink">$</span>
                <input
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                  inputMode="decimal"
                  placeholder={String(min)}
                  className="w-24 bg-transparent text-left text-[18px] font-medium text-ink outline-none placeholder:text-muted"
                />
              </div>
            </label>
            <p className="-mt-1 text-right text-[12px] text-muted">
              {t('cards.depositHint', { min, currency: 'USD' })} · {t('cards.rechargeFee')} {feePct}
              %
            </p>

            <Row label={`${t('cards.rechargeFee')} (${feePct}%)`} value={`$${fmt(fee)}`} />
            {feeRepricing ? (
              <Row label={t('wallet.txCommission')} value={<FeeValueSkeleton />} />
            ) : (
              netFullFee > 0 && (
                <Row
                  label={t('wallet.txCommission')}
                  value={feeDisplay > 0 ? `$${fmt(feeDisplay)}` : t('cards.networkFree')}
                  accent={feeDisplay === 0}
                />
              )
            )}
            {!feeRepricing && feeDiscount > 0 && (
              <Row
                label={t('cards.networkDiscount')}
                value={`−$${fmt(feeDiscount)}`}
                accent
              />
            )}
            <Row
              label={t('cards.youPay')}
              value={feeRepricing ? <FeeValueSkeleton wide /> : `$${fmt(totalWithFee)}`}
              accent={!feeRepricing}
            />

            {!feeRepricing && (
              <NetworkFeeHint
                freeFrom={policy?.freeFromUsd ?? null}
                halfFrom={policy?.halfFromUsd ?? null}
                amount={amt}
                charged={feeDisplay}
                canReachFree={policy?.freeFromUsd != null && reachable(policy.freeFromUsd)}
                canReachHalf={policy?.halfFromUsd != null && reachable(policy.halfFromUsd)}
                onPickFree={(v) => setAmount(String(v))}
              />
            )}

            <div className="mt-1">
              <span className="text-[13px] text-muted">{t('cards.payFrom')}</span>
              {sources.length === 0 ? (
                <p className="mt-2 flex items-center gap-1.5 text-[13px] font-medium text-[#b25e00]">
                  <WarningIcon size={15} />
                  {t('cards.noStablecoin')}
                </p>
              ) : (
                <div className="mt-2">
                  <SourceSelect
                    sources={sources}
                    value={source?.key ?? null}
                    onChange={setSourceKey}
                    required={payTotal}
                  />
                </div>
              )}
              {!busy && feeFresh && amountOk && source && source.balance < totalReserved && (
                <p className="mt-2 flex items-center gap-1.5 text-[13px] font-medium text-[#b25e00]">
                  <WarningIcon size={15} />
                  {t('cards.insufficientForFee', { amount: fmt(totalReserved) })}
                </p>
              )}
            </div>

            {error && (
              <p className="flex items-center gap-1.5 text-[13px] font-medium text-[#ff3b30]">
                <WarningIcon size={15} />
                {error}
              </p>
            )}

            <ProgressButton
              active={busy}
              done={false}
              onClick={onConfirm}
              disabled={busy || !canPay}
              className={`mt-3 flex h-14 items-center justify-center rounded-full bg-ink text-[15px] font-medium text-white ${
                busy
                  ? 'pointer-events-none'
                  : !canPay
                    ? 'pointer-events-none opacity-40'
                    : 'active:opacity-90'
              }`}
            >
              {busy
                ? t('cards.paying')
                : feeRepricing
                  ? <FeeValueSkeleton wide />
                  : `${t('cards.payAndTopUp')} · $${fmt(totalWithFee)}`}
            </ProgressButton>
          </div>
        )}
      </BottomSheet>

      {/* Accepted — not final credit. User can leave; balance updates on webhook. */}
      {phase === 'crediting' && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[75] flex justify-center px-4 pt-[calc(var(--tg-safe-top)_+_1.25rem)]">
          <div className="animate-toast flex items-center gap-3 rounded-[20px] bg-ink px-5 py-4 text-white shadow-[0px_16px_40px_rgba(0,0,0,0.28)]">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#377dff]">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 5v14M5 12h14" stroke="white" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
            </span>
            <div className="flex flex-col">
              <span className="text-[15px] font-semibold leading-tight">
                {t('cards.topUpInProgress')}
              </span>
              <span className="text-[13px] leading-tight text-white/70">${fmt(toastAmt)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Payment landed but Banana couldn't credit — support must top up manually. */}
      <CardTopUpManualSheet
        open={phase === 'failed'}
        onClose={() => {
          setPhase('idle');
          setFailedAt(null);
          onDone();
        }}
        telegramId={userId}
        at={failedAt ?? new Date()}
        amount={toastAmt}
        supportTelegram={links?.supportTelegram}
      />

      {reauthOpen && canFetch && (
        <div className="push-enter fixed inset-0 z-[60] overflow-y-auto bg-holo">
          <ReauthFlow
            initData={initData}
            biometricEnabled={!!me?.biometricEnabled}
            onSuccess={(token) => {
              setReauthOpen(false);
              void submit(token);
            }}
            onCancel={() => setReauthOpen(false)}
          />
        </div>
      )}
    </>
  );
}

/**
 * Where the network fee goes on this top-up.
 *
 * The discount is funded by our cut of the amount, so it grows with the amount —
 * `freeFrom` is where it covers the fee outright. Only promise a threshold the
 * user's balance can actually reach: telling someone with $60 that $800 is free
 * is worse than telling them nothing.
 */
function NetworkFeeHint({
  freeFrom,
  halfFrom,
  amount,
  charged,
  canReachFree,
  canReachHalf,
  onPickFree,
}: {
  freeFrom: number | null;
  halfFrom: number | null;
  amount: number;
  charged: number;
  canReachFree: boolean;
  canReachHalf: boolean;
  onPickFree: (amount: number) => void;
}) {
  const { t } = useTranslation();
  if (freeFrom == null) return null;

  // Typing: only the distance left matters. At/above the threshold the fee row
  // already reads "free", so a hint would just repeat it.
  if (amount > 0) {
    if (charged === 0) return null;
    const left = Math.max(0, freeFrom - amount);
    const amountText = `$${fmt(left)}`;
    const message = t('cards.netToFree', { amount: fmt(left) });
    const amountAt = message.indexOf(amountText);
    const before = amountAt >= 0 ? message.slice(0, amountAt) : '';
    const after = amountAt >= 0 ? message.slice(amountAt + amountText.length) : '';
    return (
      <p className="-mt-1 text-[12px] text-muted">
        {before}
        <button
          type="button"
          onClick={() => onPickFree(freeFrom)}
          className="font-medium text-[#377dff] active:opacity-70"
        >
          {amountAt >= 0 ? amountText : message}
        </button>
        {after}
      </p>
    );
  }

  if (canReachFree) {
    return (
      <div className="-mt-1 flex flex-wrap items-center gap-2">
        <p className="text-[12px] text-muted">
          {t('cards.netFreeFrom', { amount: fmt(freeFrom) })}
        </p>
        <button
          type="button"
          onClick={() => onPickFree(freeFrom)}
          className="rounded-full bg-[#377dff]/10 px-2.5 py-1 text-[12px] font-medium text-[#377dff] active:opacity-70"
        >
          {t('cards.netUseFree', { amount: fmt(freeFrom) })}
        </button>
      </div>
    );
  }

  return (
    <p className="-mt-1 text-[12px] text-muted">
      {canReachHalf && halfFrom != null
        ? t('cards.netHalfThenFree', { half: fmt(halfFrom), free: fmt(freeFrom) })
        : t('cards.netBiggerCheaper', { amount: fmt(freeFrom) })}
    </p>
  );
}

function FeeValueSkeleton({ wide = false }: { wide?: boolean }) {
  return <span className={`skeleton inline-block h-5 rounded-md ${wide ? 'w-24' : 'w-14'}`} />;
}

function Row({ label, value, accent }: { label: string; value: ReactNode; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b border-black/[0.06] py-2.5 last:border-b-0">
      <span className="text-[14px] text-muted">{label}</span>
      <span className={`text-[15px] font-medium ${accent ? 'text-[#377dff]' : 'text-ink'}`}>
        {value}
      </span>
    </div>
  );
}
